import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { SectionHeader, StatTile, Pill, EmptyState } from "@/components/Brand";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadialBarChart,
  RadialBar,
  CartesianGrid,
} from "recharts";
import { Users, ClipboardList, Building2, Download, MapPin } from "lucide-react";

const BLUE = ["#1d4ed8", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe"];
const TRAFFIC = { Satisfied: "#16a34a", Neutral: "#facc15", Dissatisfied: "#dc2626" };
const SENTIMENT_COLOR = { "Pro-change": "#dc2626", Neutral: "#facc15", "Pro-continuity": "#16a34a" };

export default function AdminAnalytics() {
  const [wards, setWards] = useState([]);
  const [wardId, setWardId] = useState("");
  const [booths, setBooths] = useState([]);
  const [boothId, setBoothId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/wards").then((r) => {
      setWards(r.data);
      if (r.data[0]) setWardId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!wardId) return;
    setBoothId("");
    api.get(`/admin/analytics/wards/${wardId}`).then((r) => {
      setData(r.data);
      setBooths(r.data.booths || []);
    });
  }, [wardId]);

  useEffect(() => {
    if (!wardId) return;
    const url = boothId ? `/admin/analytics/wards/${wardId}?booth_id=${boothId}` : `/admin/analytics/wards/${wardId}`;
    api.get(url).then((r) => setData(r.data));
  }, [boothId, wardId]);

  const a = data?.analytics;

  const downloadCsv = async () => {
    const r = await api.get(`/admin/export/ward/${wardId}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.ward.name}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <SectionHeader
        eyebrow="Intelligence"
        title="Ward & Booth Analytics"
        action={
          <button
            disabled={!wardId || !a}
            onClick={downloadCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            <Download size={14} /> Export CSV
          </button>
        }
      />

      <div className="wi-card p-4 mb-6 flex flex-wrap items-center gap-3">
        <span className="data-label">Filter</span>
        <select
          value={wardId}
          onChange={(e) => setWardId(e.target.value)}
          data-testid="analytics-ward-select"
          className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white"
        >
          {wards.map((w) => <option key={w.id} value={w.id}>{w.name} (#{w.number})</option>)}
        </select>
        <select
          value={boothId}
          onChange={(e) => setBoothId(e.target.value)}
          data-testid="analytics-booth-select"
          className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white"
        >
          <option value="">All booths</option>
          {booths.map((b) => <option key={b.id} value={b.id}>{b.booth_number} — {b.polling_station}</option>)}
        </select>
        {data?.ward && (
          <div className="ml-auto flex items-center gap-3">
            <MapPin size={14} className="text-slate-400" />
            <div className="text-sm">
              <span className="font-semibold text-slate-900">{data.ward.name}</span>
              <span className="text-slate-500 ml-2">{data.ward.district}</span>
            </div>
          </div>
        )}
      </div>

      {!a || a.total_surveys === 0 ? (
        <EmptyState title="No survey data yet" hint="Surveyors haven't submitted for this filter." />
      ) : (
        <>
          <div className="wi-stat-row mb-6">
            <StatTile label="Total Surveys" value={a.total_surveys} icon={ClipboardList} />
            <StatTile label="Voters Mapped" value={a.voter_counts.total} icon={Users} />
            <StatTile label="Swing Indicator" value={`${a.sentiment.swing_pct}%`} hint="Pro-change + uncertain" />
            <StatTile label="Booths Covered" value={booths.length} icon={Building2} />
          </div>

          {/* Voter breakdown */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <ChartCard title="Voter Demographics" sub="By gender & age">
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { l: "Male", v: a.voter_counts.male, c: "#1d4ed8" },
                  { l: "Female", v: a.voter_counts.female, c: "#3b82f6" },
                  { l: "Youth", v: a.voter_counts.youth, c: "#60a5fa" },
                  { l: "Senior", v: a.voter_counts.senior, c: "#93c5fd" },
                ].map((x) => (
                  <div key={x.l} className="p-3 bg-slate-50 rounded-md">
                    <div className="data-label">{x.l}</div>
                    <div className="metric-value text-xl mt-1">{x.v}</div>
                  </div>
                ))}
              </div>
              <Bars data={a.demographics.age} />
            </ChartCard>

            <ChartCard title="Political Sentiment" sub="Mood of voters">
              <Donut data={a.sentiment.mood} colorMap={SENTIMENT_COLOR} />
              <div className="mt-3 text-xs text-slate-500">
                Engagement: {a.sentiment.engagement.map((e) => `${e.name} ${e.value}`).join(" · ")}
              </div>
            </ChartCard>

            <ChartCard title="Governance Satisfaction" sub="Satisfied / Neutral / Dissatisfied">
              <Donut data={a.governance.satisfaction} colorMap={TRAFFIC} />
              <div className="mt-3 text-xs text-slate-500">
                Faster responder:{" "}
                {a.governance.responds_faster.slice(0, 1).map((g) => `${g.name} (${g.value})`)}
              </div>
            </ChartCard>
          </div>

          {/* Issues ranking */}
          <div className="wi-card p-6 mb-6">
            <div className="data-label mb-1">Top Ward Issues</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Severity ranking</h3>
            <div className="space-y-2">
              {a.issues.map((it, i) => (
                <div key={it.issue} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-1 font-mono text-xs text-slate-400">#{i + 1}</div>
                  <div className="col-span-4 md:col-span-3 text-sm font-medium text-slate-900">{it.issue}</div>
                  <div className="col-span-5 md:col-span-7">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-700 rounded-full"
                        style={{ width: `${Math.min((it.score / 3) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1 text-right font-mono text-xs text-slate-700">{it.score}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Language + migrant */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <ChartCard title="Language Profile" sub="Spoken at home">
              <Bars data={a.migrant_language.languages} horizontal />
            </ChartCard>
            <ChartCard title="Migrant Distribution" sub="Native to Bengaluru?">
              <Donut data={a.migrant_language.migrant} />
            </ChartCard>
          </div>

          {/* Voting behavior */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <ChartCard title="Voting Behavior" sub="Frequency">
              <Bars data={a.voting_behavior.behavior} />
            </ChartCard>
            <ChartCard title="Main Voting Factor" sub="What drives the ballot">
              <Donut data={a.voting_behavior.main_factor} />
            </ChartCard>
          </div>

          {/* SES + safety */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <ChartCard title="Socio-Economic Profile" sub="Observer rating">
              <Donut data={a.demographics.ses} />
            </ChartCard>
            <ChartCard title="Women Safety" sub="Perception">
              <Donut data={a.women_safety.safety} />
            </ChartCard>
            <ChartCard title="Youth Concerns" sub="Top issues">
              <Bars data={a.youth.concerns} />
            </ChartCard>
          </div>

          {/* Booth heatmap */}
          <BoothHeatmap rows={a.booth_mood_heatmap} />
        </>
      )}
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div className="wi-card p-5">
      <div className="data-label">{sub}</div>
      <h3 className="text-base font-semibold text-slate-900 mt-1 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Bars({ data, horizontal }) {
  if (!data || data.length === 0) return <div className="text-xs text-slate-400">No data</div>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout={horizontal ? "vertical" : "horizontal"} margin={{ left: horizontal ? 60 : 0, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        {horizontal ? (
          <>
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={70} />
          </>
        ) : (
          <>
            <XAxis dataKey="name" />
            <YAxis />
          </>
        )}
        <Tooltip cursor={{ fill: "#f1f5f9" }} />
        <Bar dataKey="value" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Donut({ data, colorMap }) {
  if (!data || data.length === 0) return <div className="text-xs text-slate-400">No data</div>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
          {data.map((d, i) => (
            <Cell key={d.name} fill={(colorMap && colorMap[d.name]) || BLUE[i % BLUE.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function BoothHeatmap({ rows }) {
  if (!rows || rows.length === 0) return null;
  const keys = ["Pro-change", "Neutral", "Pro-continuity"];
  const max = Math.max(...rows.flatMap((r) => keys.map((k) => r[k] || 0)), 1);
  return (
    <div className="wi-card p-5">
      <div className="data-label">Booth Mood Heatmap</div>
      <h3 className="text-base font-semibold text-slate-900 mt-1 mb-3">Sentiment intensity by booth</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left data-label px-2 py-2">Booth</th>
              {keys.map((k) => <th key={k} className="text-center data-label px-2 py-2">{k}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.booth} className="border-t border-slate-100">
                <td className="px-2 py-2 font-mono text-xs">{r.booth}</td>
                {keys.map((k) => {
                  const v = r[k] || 0;
                  const intensity = v / max;
                  const color = k === "Pro-change" ? `rgba(220,38,38,${0.15 + intensity * 0.7})` : k === "Pro-continuity" ? `rgba(22,163,74,${0.15 + intensity * 0.7})` : `rgba(250,204,21,${0.15 + intensity * 0.7})`;
                  return (
                    <td key={k} className="px-2 py-2 text-center">
                      <span className="inline-block min-w-[40px] py-1 px-2 rounded text-xs font-mono" style={{ background: color, color: intensity > 0.4 ? "#0f172a" : "#475569" }}>
                        {v || "—"}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
